import styles from './ConfiguredPMS.module.css';
import RealPagePMSIcon from '../../svgIcons/RealPagePMSIcon';
import { useMemo } from 'react';
import React from 'react';
import YardiPMSIcon from '../../svgIcons/YardiPMSIcon';
import ResmanPMSIcon from '../../svgIcons/ResmanPMSIcon';
import EntrataPMSIcon from '../../svgIcons/EntrataPMSIcon';
import KnockPMSIcon from '../../svgIcons/KnowkPmsIcon';
import moment from 'moment';
import axios from 'axios';
import { useState, useEffect } from 'react';
import CustModal from '../../common/custmodal/CustModal';
import iconClose from '../../../images/icon-close.svg';
import Button from '../../common/button/Button';
import InfoIcon from '../../svgIcons/InfoIcon';
import FunnelPMSIcon from '../../svgIcons/FunnelPMSIcon';

/* =========================
   Types
========================= */

type PMSStatus = 'active' | 'paused';

interface PMSOption {
    key: string;
    icon: string | React.ReactNode;
    label: string;
    connectedDate: string;
    status: 'active' | 'failure';
}

interface PmsConfigItem {
    id?: string;
    pms_key: string;
    pms_config?: Record<string, unknown>;
    created_at?: string;
    updated_at?: string;
}

interface ConfiguredPMSProps {
    selectedLocationId?: string;
    pmsConfig?: PmsConfigItem[] | null;
    pmsLogs?: any[];
    loading?: boolean;
}

const pmsOptionsMap: Record<string, { icon: React.ReactNode; label: string }> = {
    yardi: { icon: <YardiPMSIcon />, label: 'Yardi' },
    entrata: { icon: <EntrataPMSIcon />, label: 'Entrata' },
    realpage: { icon: <RealPagePMSIcon />, label: 'RealPage OneSite' },
    resman: { icon: <ResmanPMSIcon />, label: 'ResMan' },
    knock: { icon: <KnockPMSIcon />, label: 'Knock' },
    funnel: { icon: <FunnelPMSIcon />, label: 'Funnel' },
};

const hasAnyFilledField = (pmsConfig: Record<string, unknown> | undefined): boolean => {
    if (!pmsConfig || typeof pmsConfig !== 'object') return false;
    return Object.values(pmsConfig).some(
        (val) => val != null && val !== '' && String(val).trim() !== ''
    );
};

/* =========================
   Component
========================= */

function ConfiguredPMS({ selectedLocationId, pmsConfig, pmsLogs = [], loading = false }: ConfiguredPMSProps) {
    const [failureLogs, setFailureLogs] = useState<any[]>([]);
    const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
    const [selectedPmsForLogs, setSelectedPmsForLogs] = useState<string | null>(null);

    useEffect(() => {
        if (selectedLocationId) {
            axios.get(`/propify/failure-logs/${selectedLocationId}`)
                .then(res => {
                    if (res.data?.status && res.data?.data) {
                        setFailureLogs(res.data.data);
                    } else {
                        setFailureLogs([]);
                    }
                })
                .catch(() => {
                    setFailureLogs([]);
                });
        }
    }, [selectedLocationId]);

    const logsByPms = useMemo(() => {
        const map: Record<string, any[]> = {};
        failureLogs.forEach(log => {
            if (!map[log.pms_key]) map[log.pms_key] = [];
            map[log.pms_key].push(log);
        });
        return map;
    }, [failureLogs]);
    const pmsData = useMemo<PMSOption[]>(() => {
        if (!selectedLocationId || !pmsConfig || !Array.isArray(pmsConfig)) return [];
        const connected = pmsConfig.filter(
            (pms) => pms?.id && hasAnyFilledField(pms?.pms_config)
        );
        return connected.map((pms) => {
            const opt = pmsOptionsMap[pms.pms_key] || { icon: <RealPagePMSIcon />, label: pms.pms_key };
            const dateStr = pms.updated_at || pms.created_at;
            const connectedDate = dateStr ? moment(dateStr).format('MMM DD, YYYY') : 'Connected';

            // Calculate status from pmsLogs
            const latestLog = pmsLogs
                .filter((log: any) => log.pms_key === pms.pms_key)
                .sort(
                    (a: any, b: any) =>
                        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                )[0];
            const hasFailure = latestLog?.status === 'failure';
            const status = hasFailure ? 'failure' : 'active';

            return {
                key: pms.pms_key,
                icon: opt.icon,
                label: opt.label,
                status,
                connectedDate,
            };
        });
    }, [selectedLocationId, pmsConfig, pmsLogs]);


    if (!selectedLocationId) {
        return null;
    }

    return (
        <React.Fragment>
            <section className={styles.configuredPMSWrap}>
                <div className={styles.configuredPMSTopBar}>
                    <h2>Configured PMS</h2>
                </div>
                <div className={styles.pmsGrid}>
                    {pmsData.length > 0 ? (
                        pmsData.map((opt) => (
                            <div key={opt.key} className={styles.pmsCard}>
                                {/* Header */}
                                <div className={styles.pmsCardHeader}>
                                    <div className={styles.pmsLogo}>
                                        {typeof opt.icon === 'string' ? <img src={opt.icon} alt={opt.label} /> : opt.icon}
                                    </div>
                                    <div className={styles.statusWrap}>
                                        <div className={`${styles.pmsStatus} ${opt.status === 'active' ? styles.statusActive : styles.statusFailure}`}>
                                            <span className={styles.statusPoint}></span>
                                            <span className={styles.statusLabel}>
                                                {opt.status === 'active' ? 'Active' : 'Failure'}
                                            </span>
                                        </div>
                                        <span
                                            className={`${styles.infoIconWrap} ${opt.status === 'active' ? styles.infoActive : styles.infoFailure}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedPmsForLogs(opt.key);
                                                setIsLogsModalOpen(true);
                                            }}
                                        >
                                            <InfoIcon fillColor="#191c21" width={14} height={14} />
                                        </span>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className={styles.pmsInfo}>
                                    <h3>{opt.label} </h3>
                                    <p>Connected on {opt.connectedDate}</p>
                                </div>
                            </div>
                        ))
                    ) : !loading ? (
                        <div className={styles.noPmsMessage}>
                            <p>Not configured any PMS</p>
                        </div>
                    ) : null}
                </div>
            </section>

            <CustModal
                open={isLogsModalOpen}
                setOpen={setIsLogsModalOpen}
                width="600px"
            >
                <div className={styles.logsModalContent}>
                    <div className={styles.logsModalHeader}>
                        <h2>{selectedPmsForLogs ? pmsOptionsMap[selectedPmsForLogs]?.label : ''} Connection Logs</h2>
                        <span className={styles.closeBtn} onClick={() => setIsLogsModalOpen(false)}>
                            <img src={iconClose} alt="close" />
                        </span>
                    </div>
                    <div className={styles.logsList}>
                        {selectedPmsForLogs && logsByPms[selectedPmsForLogs]?.length > 0 ? (
                            logsByPms[selectedPmsForLogs].map((log, index) => (
                                <div key={index} className={styles.logItem}>
                                    <div className={styles.logMeta}>
                                        <span className={styles.logTime}>{moment(log.created_at).format('MMM DD, YYYY hh:mm A')}</span>
                                        <span className={styles.logStatus}>Failure</span>
                                    </div>
                                    <div className={styles.logMessage}>{log.error_message}</div>
                                </div>
                            ))
                        ) : (
                            <div className={styles.noLogsMessage}>
                                <p>No failure logs found for this property.</p>
                            </div>
                        )}
                    </div>
                    <div className={styles.logsModalFooter}>
                        <Button variant="green" onClick={() => setIsLogsModalOpen(false)}>Close</Button>
                    </div>
                </div>
            </CustModal>
        </React.Fragment>
    );
}

export default ConfiguredPMS;
